// macadamia-app/src/macadamia-core/components/postits/types.ts

// ========================================
// TYPES D'ANCRAGE
// ========================================

export type PostItAnchorType = 'block' | 'page' | 'document';

export interface PostItAnchor {
    type: PostItAnchorType;

    // Ancrage à un bloc (titre, texte, image, etc.)
    blockId?: string;
    relativeOffset?: {
        x: number;  // Pixels depuis le coin supérieur gauche du bloc
        y: number;
    };

    // Ancrage à une page A4
    pageIndex?: number;
    relativePosition?: {
        x: number;  // Pourcentage (0-100) dans la page
        y: number;
    };

    // Position absolue dans le document (fallback, ancien format)
    documentPosition?: {
        x: number;  // Pixels depuis le haut du document (scroll inclus)
        y: number;
    };
}

// ========================================
// INTERFACE PRINCIPALE
// ========================================

export interface IPostIt {
    _id: string;
    dossierId: string;
    chapterId: string;
    content: string;

    // Nouveau système d'ancrage
    anchor: PostItAnchor;

    // Ancien format (rétrocompatibilité) - sera migré automatiquement
    position?: {
        x: number;  // DEPRECATED: pixels absolus depuis le viewport
        y: number;
    };

    size: {
        width: number;
        height: number;
    };
    color: 'yellow' | 'pink' | 'blue' | 'green';
    visibility: 'private' | 'shared' | 'public';
    authorId: string;
    createdAt: Date;
    updatedAt: Date;
}

export const POSTIT_COLORS = {
    yellow: {
        gradient: 'linear-gradient(135deg, #fef9c3 0%, #fde047 100%)',
        shadow: 'rgba(253, 224, 71, 0.3)',
        headerOverlay: 'rgba(254, 240, 138, 0.8)'
    },
    pink: {
        gradient: 'linear-gradient(135deg, #fce7f3 0%, #f9a8d4 100%)',
        shadow: 'rgba(249, 168, 212, 0.3)',
        headerOverlay: 'rgba(252, 231, 243, 0.8)'
    },
    blue: {
        gradient: 'linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%)',
        shadow: 'rgba(147, 197, 253, 0.3)',
        headerOverlay: 'rgba(219, 234, 254, 0.8)'
    },
    green: {
        gradient: 'linear-gradient(135deg, #d1fae5 0%, #6ee7b7 100%)',
        shadow: 'rgba(110, 231, 183, 0.3)',
        headerOverlay: 'rgba(209, 250, 229, 0.8)'
    }
} as const;

export const POSTIT_DEFAULTS = {
    WIDTH: 200,
    HEIGHT: 200,
    MIN_WIDTH: 150,
    MIN_HEIGHT: 150,
    MAX_WIDTH: 400,
    MAX_HEIGHT: 400,
    PEEK_OFFSET: 30, // Combien de pixels dépassent du bord droit
    COLOR: 'yellow' as const,
    ANCHOR_PROXIMITY_THRESHOLD: 150 // Distance max (px) pour ancrer à un bloc
};

// ========================================
// UTILITAIRES DE CONVERSION
// ========================================

/**
 * Convertit une position viewport (souris) en position relative au conteneur
 * Prend en compte le scroll interne du conteneur et sa position dans l'écran
 */
export const viewportToContainer = (
    viewportX: number,
    viewportY: number,
    containerBounds: DOMRect | null,
    scrollContainer: HTMLElement | null
): { x: number; y: number } => {
    // Fallback sur window si pas de conteneur (mode legacy)
    if (!containerBounds || !scrollContainer) {
        return {
            x: viewportX + window.scrollX,
            y: viewportY + window.scrollY
        };
    }

    // Calcul : (Position souris - Position Coin Container) + Scroll Actuel du Container
    return {
        x: (viewportX - containerBounds.left) + scrollContainer.scrollLeft,
        y: (viewportY - containerBounds.top) + scrollContainer.scrollTop
    };
};

/**
 * Convertit une position container (stockée) en position viewport (pour comparaison avec getBoundingClientRect)
 */
export const containerToViewport = (
    containerX: number,
    containerY: number,
    containerBounds: DOMRect | null,
    scrollContainer: HTMLElement | null
): { x: number; y: number } => {
    if (!containerBounds || !scrollContainer) {
        return {
            x: containerX - window.scrollX,
            y: containerY - window.scrollY
        };
    }

    return {
        x: (containerX - scrollContainer.scrollLeft) + containerBounds.left,
        y: (containerY - scrollContainer.scrollTop) + containerBounds.top
    };
};

/**
 * @deprecated Utiliser viewportToContainer à la place
 * Convertit une position viewport en position document (inclut le scroll window)
 */
export const viewportToDocument = (
    viewportX: number,
    viewportY: number,
    scrollX: number = window.scrollX,
    scrollY: number = window.scrollY
): { x: number; y: number } => {
    return {
        x: viewportX + scrollX,
        y: viewportY + scrollY
    };
};

/**
 * @deprecated Utiliser containerToViewport à la place
 * Convertit une position document en position viewport (exclut le scroll window)
 */
export const documentToViewport = (
    documentX: number,
    documentY: number,
    scrollX: number = window.scrollX,
    scrollY: number = window.scrollY
): { x: number; y: number } => {
    return {
        x: documentX - scrollX,
        y: documentY - scrollY
    };
};

/**
 * Calcule l'offset relatif d'un post-it par rapport à un bloc
 */
export const calculateRelativeOffset = (
    postItX: number,
    postItY: number,
    blockBounds: DOMRect
): { x: number; y: number } => {
    return {
        x: postItX - blockBounds.left,
        y: postItY - blockBounds.top
    };
};

/**
 * Migre un ancien post-it (format position) vers le nouveau format (anchor)
 */
export const migratePostItToAnchor = (postit: IPostIt): IPostIt => {
    // Si anchor existe déjà, pas de migration nécessaire
    if (postit.anchor) {
        return postit;
    }

    // Si position existe (ancien format)
    if (postit.position) {
        // Convertir en position document
        const docPos = viewportToDocument(postit.position.x, postit.position.y, 0, 0);

        return {
            ...postit,
            anchor: {
                type: 'document',
                documentPosition: {
                    x: docPos.x,
                    y: docPos.y
                }
            }
        };
    }

    // Fallback : créer un anchor par défaut
    return {
        ...postit,
        anchor: {
            type: 'document',
            documentPosition: {
                x: 0,
                y: 0
            }
        }
    };
};